import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  ViewEncapsulation,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { ImpersonationBannerComponent } from 'app/layout/common/impersonation-banner/impersonation-banner.component';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { FuseLoadingBarComponent } from '@fuse/components/loading-bar';
import {
  FuseNavigationService,
  FuseVerticalNavigationComponent,
} from '@fuse/components/navigation';
import { FuseMediaWatcherService } from '@fuse/services/media-watcher';
import { NavigationService } from 'app/core/navigation/navigation.service';
import { Navigation } from 'app/core/navigation/navigation.types';
import { LanguagesComponent } from 'app/layout/common/languages/languages.component';
import { NotificationsComponent } from 'app/layout/common/notifications/notifications.component';
import { SearchComponent } from 'app/layout/common/search/search.component';
import { UserComponent } from 'app/layout/common/user/user.component';
import { Subject, firstValueFrom, takeUntil } from 'rxjs';
import { environment } from 'environments/environment';
import { UserService } from 'app/core/user/user.service';
import { TenantBrandingService } from 'app/services/tenant-branding.service';
import {
  FuseConfig,
  FuseConfigService,
  Scheme,
  Theme,
  Themes,
} from '@fuse/services/config';
import { NgModule } from '@angular/core';

import { CommonModule } from '@angular/common';
import { ThemeSwitchComponent } from 'app/layout/common/themeswitch/themeswitch.component';
import { User } from 'app/core/user/user.types';
import { ProfileService } from 'app/services/profile.service';
import { NotificationsService } from 'app/layout/common/notifications/notifications.service';
import { Notification } from 'app/models/Notification.interface';
import { ShipmentService } from 'app/services/shipment.service';
import { TranslocoModule } from '@ngneat/transloco';
@Component({
  selector: 'classic-layout',
  styleUrls: ['./classic.component.scss'],
  templateUrl: './classic.component.html',
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    ImpersonationBannerComponent,
    FuseLoadingBarComponent,
    FuseVerticalNavigationComponent,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    LanguagesComponent,
    SearchComponent,
    ThemeSwitchComponent,
    NotificationsComponent,
    UserComponent,
    CommonModule,
    RouterModule,
    TranslocoModule,
  ],
})
export class ClassicLayoutComponent
  implements OnInit, OnDestroy, AfterViewInit
{
  private _activatedRoute = inject(ActivatedRoute);
  private _router = inject(Router);
  private _navigationService = inject(NavigationService);
  private _fuseMediaWatcherService = inject(FuseMediaWatcherService);
  private _fuseNavigationService = inject(FuseNavigationService);
  private _fuseConfigService = inject(FuseConfigService);
  private _userService = inject(UserService);
  private _changeDetectorRef = inject(ChangeDetectorRef);
  private _profileService = inject(ProfileService);
  private _notificationsService = inject(NotificationsService);
  private _shipmentService = inject(ShipmentService);
  private _tenantBrandingService = inject(TenantBrandingService);

  isScreenSmall: boolean;
  navigation: Navigation;
  private _unsubscribeAll: Subject<any> = new Subject<any>();
  private readonly _branding = this._tenantBrandingService.branding;
  portalName = this._branding.portalName;
  companyName = this._branding.companyName;
  companyWebsite = this._branding.companyWebsite;
  circleWebsite = this._branding.circleWebsite;
  creator = this._branding.creator;
  portalLogo = this._branding.portalLogo;
  portalLogoWhite = this._branding.portalLogoWhite;
  appTitle = this._branding.appTitle;

  config: FuseConfig;
  layout: string;
  scheme: 'dark' | 'light';
  theme: string;
  themes: Themes;
  user: User;
  status = true;
  private readonly _pendingBadgeUpdates = new Map<string, number>();
  private _shipmentsBadgeRefreshInProgress = false;
  private _notificationsBadgeInitialized = false;

  /** Inserted by Angular inject() migration for backwards compatibility */
  constructor(...args: unknown[]);

  /**
   * Constructor
   */
  constructor() {}

  // -----------------------------------------------------------------------------------------------------
  // @ Accessors
  // -----------------------------------------------------------------------------------------------------

  /**
   * Getter for current year
   */
  get currentYear(): number {
    return new Date().getFullYear();
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Lifecycle hooks
  // -----------------------------------------------------------------------------------------------------

  /**
   * On init
   */
  ngOnInit(): void {
    // Subscribe to navigation data
    this._navigationService.navigation$
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe((navigation: Navigation) => {
        this.navigation = navigation;
        this.refreshMenuBadges();
      });

    // Subscribe to media changes
    this._fuseMediaWatcherService.onMediaChange$
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe(({ matchingAliases }) => {
        // Check if the screen is small
        this.isScreenSmall = !matchingAliases.includes('md');
      });

    this._fuseConfigService.config$
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe((config: FuseConfig) => {
        // Store the config
        this.config = config;
        this.scheme = config.scheme == 'dark' ? 'dark' : 'light';
      });

    const savedScheme = localStorage.getItem('userTheme') as Scheme;
    if (savedScheme) {
      this.setScheme(savedScheme);
    }

    this._userService.user$
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe((user: User) => {
        this.user = user;

        // Mark for check
        this._changeDetectorRef.markForCheck();
      });

    this._profileService
      .getProfileInfo()
      .then((data: any) => {
        const status = data?.userInfo?.isVerified;
        this.status = status;
        console.log('User status:', status);

        // Mark for check to update the UI
        this._changeDetectorRef.markForCheck();
      })
      .catch(error => {
        console.error('Error fetching user profile:', error);
      });

    this.refreshMenuBadges();
  }

  /**
   * On destroy
   */
  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this._unsubscribeAll.next(null);
    this._unsubscribeAll.complete();
  }

  ngAfterViewInit(): void {
    setTimeout(() => this.flushPendingBadgeUpdates());
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Public methods
  // -----------------------------------------------------------------------------------------------------

  /**
   * Toggle navigation
   *
   * @param name
   */
  toggleNavigation(name: string): void {
    // Get the navigation
    const navigation =
      this._fuseNavigationService.getComponent<FuseVerticalNavigationComponent>(
        name
      );

    if (navigation) {
      // Toggle the opened status
      navigation.toggle();
    }
  }
  navigateToCreateQuoteRequest(): void {
    this._router.navigate(['/quote-requests/create']);
  }

  navigateToAiQuoteRequest(): void {
    this._router.navigate(['/ai-quote-request']);
  }

  checkForTestEnvironment(): boolean {
    return (
      environment.apiUrl.includes('test-portal-api') ||
      environment.apiUrl.includes('localhost')
    );
  }

  /**
   * Set the scheme on the config
   *
   * @param scheme
   */
  setScheme(scheme: Scheme): void {
    this._fuseConfigService.config = { scheme };
    localStorage.setItem('userTheme', scheme); // Save the scheme in localStorage
    this.scheme = scheme == 'dark' ? 'dark' : 'light';
  }
  toggleScheme(): void {
    this.scheme = this.scheme === 'dark' ? 'light' : 'dark';
    this.setScheme(this.scheme);
    //refresh page
    window.location.reload();
    //  document.documentElement.classList.toggle('dark', this.theme === 'dark');
  }

  /**
   * Set the theme on the config
   *
   * @param theme
   */
  setTheme(theme: Theme): void {
    this._fuseConfigService.config = { theme };
  }

  private refreshMenuBadges(): void {
    this.setupNotificationsBadge();
    this.updateQuoteRequestAndShipmentBadges();
    setTimeout(() => this.flushPendingBadgeUpdates());
  }

  private setupNotificationsBadge(): void {
    if (this._notificationsBadgeInitialized) {
      return;
    }

    this._notificationsBadgeInitialized = true;

    this._notificationsService.notifications$
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe({
        next: notifications => this.applyInboxBadge(notifications),
        error: error => {
          console.error('Error receiving notifications:', error);
          this.applyInboxBadge([]);
        },
      });

    firstValueFrom(this._notificationsService.getNotifications(2))
      .then(notifications => this.applyInboxBadge(notifications))
      .catch(error => {
        console.error('Error loading notifications for badge:', error);
        this.applyInboxBadge([]);
      });
  }

  private applyInboxBadge(
    notifications: Notification[] | null | undefined
  ): void {
    const unreadCount = (notifications ?? []).filter(notification =>
      this.isNotificationUnread(notification)
    ).length;

    this.updateNavigationItemBadge('home.inbox', unreadCount);
    this.flushPendingBadgeUpdates();
  }

  private isNotificationUnread(
    notification: Notification | null | undefined
  ): boolean {
    if (!notification) {
      return false;
    }

    if (notification.isArchived) {
      return false;
    }

    // if (notification.docType === 'Task') {
    //   return false;
    // }

    return !!notification.isUnread;
  }

  private async updateQuoteRequestAndShipmentBadges(): Promise<void> {
    if (this._shipmentsBadgeRefreshInProgress) {
      return;
    }

    this._shipmentsBadgeRefreshInProgress = true;

    try {
      const counts = await this._shipmentService.getMenuBadgeCounts();

      this.updateNavigationItemBadge(
        'sales.quote-requests.new',
        this.coerceBadgeValue(counts['sales.quote-requests.new'])
      );
      //   console.log('Badge count for sales.quote-requests.new:', counts['sales.quote-requests.new']);
      this.updateNavigationItemBadge(
        'sales.quote-requests.archived',
        this.coerceBadgeValue(counts['sales.quote-requests.archived'])
      );
      //   this.updateNavigationItemBadge(
      //     'sales.quote-requests',
      //     this.coerceBadgeValue(counts['sales.quote-requests'])
      //   );
      this.updateNavigationItemBadge(
        'sales.quote-requests.operationInProgress',
        this.coerceBadgeValue(
          counts['sales.quote-requests.operationInProgress']
        )
      );
      this.updateNavigationItemBadge(
        'operations.completed',
        this.coerceBadgeValue(counts['operations.completed'])
      );
      //   this.updateNavigationItemBadge(
      //     'sales.quote-requests.closed',
      //     this.coerceBadgeValue(counts['sales.quote-requests.closed'])
      //   );
      //   this.updateNavigationItemBadge(
      //     'operations.all',
      //     this.coerceBadgeValue(counts['operations.all'])
      //   );
    } catch (error) {
      console.error('Error updating shipment menu badges:', error);
    } finally {
      this._shipmentsBadgeRefreshInProgress = false;
      this.flushPendingBadgeUpdates();
    }
  }

  private coerceBadgeValue(value: unknown): number {
    if (typeof value === 'number') {
      return value;
    }

    if (typeof value === 'string') {
      const parsed = Number(value);
      return Number.isNaN(parsed) ? 0 : parsed;
    }

    return 0;
  }

  private updateNavigationItemBadge(itemId: string, count: number): void {
    const sanitizedCount = Number.isFinite(count)
      ? Math.max(0, Math.trunc(count))
      : 0;

    const navComponent =
      this._fuseNavigationService.getComponent<FuseVerticalNavigationComponent>(
        'mainNavigation'
      );

    if (!navComponent) {
      this._pendingBadgeUpdates.set(itemId, sanitizedCount);
      return;
    }

    const navigation = navComponent.navigation ?? [];
    const item = this._fuseNavigationService.getItem(itemId, navigation);

    if (!item) {
      return;
    }

    if (!item.badge) {
      item.badge = {
        title: sanitizedCount.toString(),
        classes: 'px-2 bg-emerald-300 text-black rounded',
      };
    } else {
      item.badge = {
        ...item.badge,
        title: sanitizedCount.toString(),
      };
    }

    navComponent.refresh();
    this._pendingBadgeUpdates.delete(itemId);
  }

  private flushPendingBadgeUpdates(): void {
    if (this._pendingBadgeUpdates.size === 0) {
      return;
    }

    const entries = Array.from(this._pendingBadgeUpdates.entries());

    for (const [itemId, count] of entries) {
      this.updateNavigationItemBadge(itemId, count);
    }
  }

  resendVerificationEmail(): void {
    this._profileService
      .resendVerificationEmail(this.user.email)
      .then(response => {
        console.log('Verification email resent successfully', response);
        // Handle response here, e.g., show a success message
      })
      .catch(error => {
        console.error('Error resending verification email', error);
        // Handle error here, e.g., show an error message
      });
  }
}
